import React from 'react';
import { Box, Stack, Typography } from '@mui/material';

import type {
  PortalUserAuditChange,
  PortalUserAuditSnapshot,
} from '../../../store/modules/merchant/types';
import {
  toPortalUserAuditChangeDisplays,
  toPortalUserSnapshotDisplayRows,
  type PortalUserAuditChangeDisplay,
} from '../portalUserAuditChangeCopy';

type ChangesProps = {
  title?: string;
  changes: PortalUserAuditChange[];
};

type SnapshotSummaryProps = {
  title: string;
  snapshot: PortalUserAuditSnapshot | null;
};

const ColumnHeader = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="small" fontWeight="semibold" color="grey.600" mb={0.75}>
    {children}
  </Typography>
);

const ScalarValue = ({ children }: { children: string }) => (
  <Typography variant="medium" sx={{ wordBreak: 'break-word' }}>
    {children}
  </Typography>
);

const ListValue = ({ items }: { items: string[] }) =>
  items.length === 0 ? (
    <Typography variant="medium" color="grey.600">
      (none)
    </Typography>
  ) : (
    <Stack spacing={0.5} component="ul" sx={{ m: 0, pl: 2.25, listStyleType: 'disc' }}>
      {items.map((item) => (
        <Typography key={item} component="li" variant="medium" sx={{ wordBreak: 'break-word' }}>
          {item}
        </Typography>
      ))}
    </Stack>
  );

const ChangeComparison = ({ change }: { change: PortalUserAuditChangeDisplay }) => (
  <Box
    sx={{
      border: '2px solid',
      borderColor: 'grey.400',
      borderRadius: 2,
      p: 2,
    }}
  >
    <Typography variant="medium" fontWeight="semibold" color="text.primary" mb={1.5}>
      {change.label}
    </Typography>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
        gap: 2,
      }}
    >
      <Box>
        <ColumnHeader>Previous</ColumnHeader>
        {Array.isArray(change.previous) ? (
          <ListValue items={change.previous} />
        ) : (
          <ScalarValue>{change.previous}</ScalarValue>
        )}
      </Box>
      <Box>
        <ColumnHeader>New</ColumnHeader>
        {Array.isArray(change.next) ? (
          <ListValue items={change.next} />
        ) : (
          <ScalarValue>{change.next}</ScalarValue>
        )}
      </Box>
    </Box>
  </Box>
);

export const PortalUserAuditChanges = ({ title = 'Changes', changes }: ChangesProps) => {
  const displays = toPortalUserAuditChangeDisplays(changes);

  return (
    <Box>
      <Typography variant="large" fontWeight="semibold" color="text.primary" mb={1.5}>
        {title}
      </Typography>
      {displays.length === 0 ? (
        <Typography variant="medium" color="grey.600">
          No changes recorded.
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {displays.map((change) => (
            <ChangeComparison key={change.field} change={change} />
          ))}
        </Stack>
      )}
    </Box>
  );
};

export const PortalUserAuditSnapshotSummary = ({ title, snapshot }: SnapshotSummaryProps) => {
  const rows = toPortalUserSnapshotDisplayRows(snapshot);

  return (
    <Box>
      <Typography variant="large" fontWeight="semibold" color="text.primary" mb={1.5}>
        {title}
      </Typography>
      {rows.length === 0 ? (
        <Typography variant="medium" color="grey.600">
          No details recorded.
        </Typography>
      ) : (
        <Box
          sx={{
            border: '2px solid',
            borderColor: 'grey.400',
            borderRadius: 2,
            p: 2,
          }}
        >
          <Stack spacing={1.5}>
            {rows.map((row) => (
              <Box key={row.label}>
                <Typography variant="small" fontWeight="semibold" color="grey.600">
                  {row.label}
                </Typography>
                <Typography variant="medium" sx={{ wordBreak: 'break-word' }}>
                  {row.value}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
};
